// Approved Technology page content — Oxyvin "Technology page" (27 Aug 2026).
// Content is verbatim from the approved Word document. Do not alter meaning, claims or
// factual statements without approval — only presentation (headings, grouping, layout).

export const technologyPage = {
  seo: {
    title: "Customer Engagement Technology & Contact Center Platform | Oxyvin",
    description: "Explore how Oxyvin combines contact-center technology, workforce management, reporting and multichannel capabilities to deliver flexible customer engagement.",
  },
  hero: {
    heading: "Technology that gives people the power to engage at scale.",
    lede: "Oxyvin combines customer engagement professionals with enterprise-grade contact-center technology to create visibility, control and flexibility across every engagement.",
    tagline: "Technology enables the operation. People make the conversation happen.",
  },
  sections: [
    {
      kind: "intro",
      heading: "Technology should make customer engagement simpler.",
      paragraphs: ["A customer engagement operation becomes difficult to manage when people, campaigns, systems and reporting operate separately."],
      itemsIntro: "Oxyvin brings the operational technology layer together with the customer engagement workforce. This creates a connected environment for:",
      items: ["Campaign execution", "Agent management", "Customer conversations", "Monitoring", "Reporting", "Quality", "Operational visibility"],
    },
    {
      kind: "journey",
      heading: "The Oxyvin technology model",
      items: ["Customer", "Engagement Channels", "Customer Engagement Professionals", "Contact-Center Platform", "Campaigns + Workflows + Reporting", "Client Teams"],
      note: "The objective is to connect customer conversations with the people and systems responsible for managing them.",
    },
    {
      kind: "grid",
      heading: "Contact-center technology at the core.",
      intro: "Oxyvin uses an enterprise-grade contact-center platform to support customer engagement operations. Depending on the engagement, the technology layer can support:",
      items: [
        ["Campaign Management", "Create and manage structured customer engagement campaigns."],
        ["Agent Management", "Manage agent availability, activity and operational status."],
        ["Call Management", "Support structured outbound and inbound customer conversations."],
        ["Call Recording", "Where enabled and appropriate, conversations can be recorded for operational review, quality and agreed business requirements."],
        ["Reporting", "Provide campaign and operational reporting to help teams understand activity and performance."],
        ["Monitoring", "Give operational teams visibility into ongoing customer engagement activity."],
      ],
    },
    {
      kind: "grid",
      heading: "One operating layer. Multiple customer journeys.",
      intro: "Oxyvin technology can support different engagement models.",
      items: [
        ["Sales", "Lead follow-up, qualification, outreach and appointment setting."],
        ["Customer Support", "Inbound enquiries, callbacks and defined customer service workflows."],
        ["Admissions", "Student enquiries, qualification, follow-up and counselling support."],
        ["Campaigns", "Structured outbound customer engagement programs."],
        ["Peak Support", "Additional engagement capacity during periods of increased demand."],
        ["Multilingual Engagement", "Support customer conversations across different language requirements."],
      ],
    },
    {
      kind: "intro",
      heading: "Visibility from conversation to performance.",
      paragraphs: ["Customer engagement shouldn't operate as a black box."],
      itemsIntro: "Depending on the engagement model, clients can have visibility into:",
      items: ["Campaign activity", "Agent login status", "Call activity", "Call outcomes", "Historical performance", "Call recordings where enabled", "Operational reports"],
      closing: "This allows customer engagement to be managed using real operational information rather than assumptions.",
    },
    {
      kind: "intro",
      heading: "Built for distributed customer engagement.",
      paragraphs: ["Oxyvin's workforce doesn't have to be concentrated in a single physical contact center. The technology layer helps connect distributed customer engagement professionals with the processes and operating environment required by the campaign. This creates a model where:"],
      readMore: { label: "See how this worked in practice: Building a Multilingual Customer Engagement Workforce for a Financial Services Company", to: "/resources/bfsi-multilingual-customer-engagement" },
    },
    {
      kind: "quote",
      text: "People can be distributed, while operations remain centrally coordinated.",
      closing: "This is particularly useful when customer engagement requires multilingual professionals who may not be available in one geographic location.",
    },
    {
      kind: "twoModel",
      heading: "Client-controlled or Oxyvin-managed.",
      intro: "Oxyvin can support different operating models depending on the client's requirements.",
      options: [
        ["Client-Controlled Model", "The client provides access to its own CRM, contact-center platform, dialer or other required systems. This allows the client to retain control over its existing technology environment and processes."],
        ["Oxyvin-Managed Model", "Where agreed under the engagement and applicable contractual arrangements, Oxyvin can provide the supporting customer engagement technology environment and operational management."],
      ],
      itemsIntro: "The appropriate model depends on:",
      items: ["Customer requirements", "Systems", "Data", "Security", "Compliance", "Operational processes"],
    },
    {
      kind: "intro",
      heading: "Security and data handling",
      paragraphs: ["Customer engagement technology must protect the information flowing through it."],
      itemsIntro: "Oxyvin's operating model can support appropriate controls around:",
      items: ["Access management", "Role-based access", "Customer data handling", "Call recordings", "Data visibility", "Operational monitoring", "Masking of customer information where applicable"],
      readMore: { label: "See our Data Protection, Security & Compliance page", to: "/data-protection-security-compliance" },
    },
    {
      kind: "intro",
      heading: "Client-controlled environments",
      paragraphs: ["For client-managed environments, the client can apply its own:"],
      items: ["Endpoint controls", "DLP policies", "CRM permissions", "Access policies", "Security monitoring"],
      closing: "For Oxyvin-managed environments, controls are implemented according to the agreed engagement, contractual requirements and applicable policies. Technology controls should always be aligned with the specific customer engagement and data requirements.",
    },
    {
      kind: "journey",
      heading: "Multichannel customer engagement",
      intro: "A customer doesn't always answer a phone call. That's why Oxyvin's engagement model can extend beyond voice where the engagement requires it. For example:",
      items: ["Outbound Call", "No Answer", "Supported Digital Follow-Up", "WhatsApp, Email or Other Approved Channel", "Continue the Conversation"],
      note: "This supports the Oxyvin principle: The Right Conversation. The Right Time. The Right Language. The Right Channel. Channels depend on client requirements, customer journey, applicable processes, approved technology and engagement scope.",
    },
    {
      kind: "grid",
      dark: true,
      heading: "Technology + people + process",
      intro: "Technology alone doesn't create a successful customer engagement operation. Oxyvin brings together three layers:",
      items: [
        ["People", "Professionals who conduct the customer conversations."],
        ["Process", "Scripts, workflows, escalation paths, quality processes and campaign rules."],
        ["Technology", "The systems that enable communication, management, visibility and reporting."],
      ],
      closing: "Together: People + Process + Technology create a customer engagement capability that can scale with business requirements.",
    },
    {
      kind: "intro",
      heading: "Designed for flexibility",
      paragraphs: ["Traditional contact-center infrastructure often assumes relatively stable demand. But customer engagement isn't always predictable."],
      itemsIntro: "Oxyvin's technology-enabled model supports changing requirements such as:",
      items: ["Campaign launch → Add capacity", "Demand increases → Scale engagement", "Peak period → Support additional volume", "Demand normalizes → Scale down", "Long-term requirement → Continue and expand the engagement"],
      closing: "This flexibility is central to how Oxyvin operates.",
    },
    {
      kind: "intro",
      heading: "Technology shouldn't replace the human conversation.",
      paragraphs: ["Oxyvin's philosophy is deliberately simple. We don't believe every customer interaction needs to be automated. Some conversations require:"],
      items: ["Context", "Empathy", "Explanation", "Regional language", "Judgment", "Human interaction"],
      closing: "Technology should help those conversations happen more efficiently, consistently and visibly. Technology provides the control. People provide the conversation.",
    },
    {
      kind: "intro",
      heading: "Technology that supports your existing organization.",
      paragraphs: ["Oxyvin doesn't require every client to replace its existing technology environment. Where appropriate, Oxyvin can operate within a client's existing:"],
      items: ["CRM", "Contact-center platform", "Dialer", "Reporting environment", "Security controls", "Workflow"],
      closing: "Or provide the supporting technology layer as part of the agreed engagement. The objective is to fit technology around the customer's operating model—not force the customer into a fixed technology model.",
    },
    {
      kind: "journey",
      heading: "From conversation to visibility",
      items: ["Customer Data", "Campaign", "Customer Engagement Professional", "Conversation", "Outcome", "Reporting", "Business Decision"],
      note: "The technology layer connects each step so that customer engagement can be monitored, measured and improved.",
    },
    {
      kind: "grid",
      heading: "Our technology principles",
      items: [
        ["Simple", "Technology should simplify operations rather than create unnecessary complexity."],
        ["Visible", "Customer engagement activity should be measurable and observable."],
        ["Flexible", "Technology should adapt to different operating models and customer requirements."],
        ["Secure", "Customer data and access should be managed using appropriate controls."],
        ["Human-Centered", "Technology should enable better customer conversations—not eliminate meaningful human interaction where it matters."],
      ],
    },
  ],
  finalCta: {
    heading: "Build the technology layer around your customer engagement model.",
    text: "Whether you're adding multilingual capacity, launching a campaign, supporting customers or extending an existing contact center, Oxyvin can help combine the people, process and technology required.",
  },
};
